import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Play, FileDown } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, LabelList } from "recharts";
import { api, errMsg, download } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Field, Select, PastelCard } from "@/components/common";
import { ClipboardCheck } from "lucide-react";

const PERIODS = { testing: [["daily", "Daily"], ["weekly", "Weekly"], ["monthly", "Monthly"], ["custom", "Date Range"]], medreq: [["daily", "Daily"], ["weekly", "Weekly"], ["custom", "Date Range"]] };
const OTHER = { testing: "Other — Enter Manually", medreq: "Other — Enter Manually" };
const TONES = ["blue", "green", "lavender", "amber"];
const todayET = () => new Date().toLocaleDateString("en-CA", { timeZone: "America/New_York" });

function Chart({ b }) {
  const data = b.data.slice(0, 15).map(([label, value]) => ({ label, value }));
  return (
    <section className="panel p-4" data-testid="ops-chart">
      <h3 className="mb-2 text-sm font-semibold text-[#20336F] dark:text-blue-200">{b.title}</h3>
      {data.length ? (
        <ResponsiveContainer width="100%" height={Math.max(120, data.length * 30 + 30)}>
          <BarChart data={data} layout="vertical" margin={{ left: 10, right: 30 }}>
            <CartesianGrid strokeDasharray="3 3" horizontal={false} />
            <XAxis type="number" allowDecimals={false} fontSize={11} />
            <YAxis type="category" dataKey="label" width={170} fontSize={11} />
            <Tooltip />
            <Bar dataKey="value" fill="#283F91" radius={[0, 4, 4, 0]}><LabelList dataKey="value" position="right" fontSize={11} /></Bar>
          </BarChart>
        </ResponsiveContainer>
      ) : <p className="py-6 text-center text-sm text-[var(--muted-ink)]">No data</p>}
    </section>
  );
}

function Tbl({ b }) {
  return (
    <section className="panel overflow-hidden" data-testid="ops-table">
      <h3 className="border-b px-4 py-2.5 text-sm font-semibold text-[#20336F] dark:text-blue-200">{b.title}</h3>
      <div className="max-h-[480px] overflow-auto">
        <table className="tbl w-full">
          <thead className="sticky top-0 bg-[#DCEBFA] dark:bg-white/10"><tr>{b.columns.map((c) => <th key={c.key} className="!text-[#20336F] dark:!text-blue-200">{c.label}</th>)}</tr></thead>
          <tbody>
            {b.rows.map((r, i) => <tr key={i}>{b.columns.map((c) => <td key={c.key}>{r[c.key] ?? "—"}</td>)}</tr>)}
            {!b.rows.length && <tr><td colSpan={b.columns.length} className="py-6 text-center text-[var(--muted-ink)]">No records for the selected period and filters.</td></tr>}
          </tbody>
        </table>
      </div>
    </section>
  );
}

export function OpsReports({ module }) {
  const { meta, can } = useApp();
  const [period, setPeriod] = useState("daily");
  const [d, setD] = useState(todayET());
  const [range, setRange] = useState({ start: todayET(), end: todayET() });
  const [f, setF] = useState({});
  const [opts, setOpts] = useState({ locations: [], employees: [], items: [] });
  const [rep, setRep] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    setRep(null); setF({}); setPeriod("daily");
    if (module === "testing") {
      Promise.all([api.get("/rotation/locations"), api.get("/rotation/services")]).then(([l, s]) =>
        setOpts({ locations: l.data, employees: meta?.staff || [], items: s.data }));
    } else {
      api.get("/med-requests/options").then((r) => setOpts({ locations: [...new Map(r.data.employees.flatMap((e) => e.locations).concat(meta?.locations || []).map((l) => [l.id, l])).values()], employees: r.data.employees, items: r.data.medications }));
    }
  }, [module, meta]);
  const display = () => ({
    Location: opts.locations.find((l) => l.id === f.location_id)?.name, Employee: opts.employees.find((e) => e.id === f.employee_id)?.name,
    [module === "testing" ? "Service" : "Medication"]: f.item === OTHER[module] ? "Custom entries" : f.item, Status: f.status,
  });
  const body = () => ({ module, period, date: d, start: range.start, end: range.end, filters_display: display(),
    filters: { location_id: f.location_id, employee_id: f.employee_id, status: f.status, [module === "testing" ? "service" : "medication"]: f.item } });
  const run = async () => {
    setBusy(true);
    try { setRep((await api.post("/ops-reports/run", body())).data); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const pdf = async () => {
    try { await download("/ops-reports/pdf", { method: "post", body: body(), filename: `${module}_${period}_${rep.start}_${rep.end}.pdf` }); } catch (e) { toast.error("PDF export not permitted or failed"); }
  };
  return (
    <div>
      <div className="panel space-y-4 p-4" data-testid={`ops-form-${module}`}>
        <div className="flex flex-wrap items-end gap-3">
          <div><span className="lbl">Report period</span>
            <div className="mt-1.5 flex overflow-hidden rounded-md border">
              {PERIODS[module].map(([k, l]) => <button key={k} onClick={() => { setPeriod(k); setRep(null); }} data-testid={`ops-period-${k}`}
                className={`px-3 py-1.5 text-xs font-semibold transition-colors ${period === k ? "bg-[#283F91] text-white" : "bg-[var(--panel)] text-[var(--muted-ink)] hover:text-[#283F91]"}`}>{l}</button>)}
            </div></div>
          {period === "custom" ? (<>
            <Field label="From"><input type="date" className="inp w-40" value={range.start} onChange={(e) => setRange({ ...range, start: e.target.value })} data-testid="ops-start" /></Field>
            <Field label="To"><input type="date" className="inp w-40" value={range.end} onChange={(e) => setRange({ ...range, end: e.target.value })} data-testid="ops-end" /></Field>
          </>) : <Field label={period === "daily" ? "Report date" : period === "weekly" ? "Any date in week" : "Any date in month"}><input type="date" className="inp w-44" value={d} onChange={(e) => setD(e.target.value)} data-testid="ops-date" /></Field>}
        </div>
        <div className="grid grid-cols-2 gap-2 md:grid-cols-4" data-testid="ops-filters">
          <Select value={f.location_id} onChange={(v) => setF({ ...f, location_id: v })} placeholder="All authorized locations" testid="ops-filter-location" options={opts.locations.map((l) => ({ value: l.id, label: l.name }))} />
          <Select value={f.employee_id} onChange={(v) => setF({ ...f, employee_id: v })} placeholder="All employees" testid="ops-filter-employee" options={opts.employees.map((e) => ({ value: e.id, label: e.name }))} />
          <Select value={f.item} onChange={(v) => setF({ ...f, item: v })} placeholder={module === "testing" ? "All services" : "All medications"} testid="ops-filter-item" options={[...opts.items, { value: OTHER[module], label: "Custom entries" }]} />
          {module === "testing" && <Select value={f.status} onChange={(v) => setF({ ...f, status: v })} placeholder="All statuses" testid="ops-filter-status" options={["Assigned", "In Progress", "Completed", "Cancelled"]} />}
        </div>
        <div className="flex flex-wrap gap-2">
          <button className="btn btn-primary" onClick={run} disabled={busy} data-testid="ops-run-btn"><Play size={14} />Generate report</button>
          {rep && can("action:export") && <button className="btn btn-ghost" onClick={pdf} data-testid="ops-pdf-btn"><FileDown size={14} />Export PDF (US Letter)</button>}
        </div>
      </div>
      {rep && (
        <div className="mt-6 space-y-4" data-testid="ops-report-output">
          <div className="flex items-center gap-4">
            <img src="/brand/logo.png" alt="" className="h-12 rounded bg-white p-1" />
            <div>
              <h2 className="text-xl font-bold text-[#20336F] dark:text-blue-200" data-testid="ops-report-title">{rep.title}</h2>
              <p className="text-xs text-[var(--muted-ink)]">{rep.period_label} · Generated {rep.generated_at} by {rep.generated_by} · Filters: {Object.entries(rep.filters_display).map(([k, v]) => `${k}: ${v}`).join(", ") || "All authorized locations"}</p>
            </div>
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3" data-testid="ops-summary">
            {rep.summary.map((s, i) => <PastelCard key={s.label} icon={ClipboardCheck} tone={TONES[i % 4]} label={s.label} value={s.value} testid={`ops-sum-${i}`} />)}
          </div>
          <div className="grid gap-4 xl:grid-cols-2">
            {rep.blocks.map((b, i) => (b.type === "chart" ? <Chart key={i} b={b} /> : <div key={i} className={b.columns.length > 5 || b.rows.length > 10 ? "xl:col-span-2" : ""}><Tbl b={b} /></div>))}
          </div>
        </div>
      )}
    </div>
  );
}
